import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Building2, AlertCircle, FileText, CheckCircle, Ruler } from 'lucide-react';
import { formatInchesToFtIn } from '@/utils/imperialUnits';

const PublicSpecard = () => {
  const { typeId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportData, setReportData] = useState({
    description: '',
    reportedByName: '',
    reportedByEmail: ''
  });

  useEffect(() => {
    fetchData();
  }, [typeId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/window-type/detail/${typeId}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load window type details');
    } finally {
      setLoading(false);
    }
  };

  const handleReportIssue = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reports`, {
        ...reportData,
        windowTypeId: typeId
      });
      toast.success('Issue reported successfully');
      setShowReportDialog(false);
      setReportData({ description: '', reportedByName: '', reportedByEmail: '' });
    } catch (error) {
      toast.error('Failed to report issue');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-12">
            <p className="text-lg text-slate-600">Window not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { opening, windowType, project } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-speccard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Specard</h1>
              <p className="text-sm text-slate-600">Window Specification Details</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Window Label & Project */}
        <Card className="mb-6" data-testid="window-info-card">
          <CardHeader>
            <CardTitle className="text-3xl">{opening.label}</CardTitle>
            <CardDescription className="text-base">
              {project.name} • {project.address}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Window Type Specifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Window Specifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dimensions */}
            {(windowType.windowWidth_in || windowType.windowHeight_in || windowType.roWidth_in || windowType.roHeight_in) && (
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Ruler className="h-5 w-5" />
                  Dimensions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(windowType.windowWidth_in && windowType.windowHeight_in) && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Window Size</p>
                      <p className="text-lg font-medium">
                        {formatInchesToFtIn(windowType.windowWidth_in)} × {formatInchesToFtIn(windowType.windowHeight_in)}
                      </p>
                      <p className="text-sm text-slate-500">
                        ({windowType.windowWidth_in.toFixed(2)} × {windowType.windowHeight_in.toFixed(2)} in)
                      </p>
                    </div>
                  )}
                  {(windowType.roWidth_in && windowType.roHeight_in) && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Rough Opening</p>
                      <p className="text-lg font-medium">
                        {formatInchesToFtIn(windowType.roWidth_in)} × {formatInchesToFtIn(windowType.roHeight_in)}
                      </p>
                      <p className="text-sm text-slate-500">
                        ({windowType.roWidth_in.toFixed(2)} × {windowType.roHeight_in.toFixed(2)} in)
                      </p>
                    </div>
                  )}
                  {(windowType.glassWidth_in && windowType.glassHeight_in) && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Size</p>
                      <p className="text-lg font-medium">
                        {formatInchesToFtIn(windowType.glassWidth_in)} × {formatInchesToFtIn(windowType.glassHeight_in)}
                      </p>
                      <p className="text-sm text-slate-500">
                        ({windowType.glassWidth_in.toFixed(2)} × {windowType.glassHeight_in.toFixed(2)} in)
                      </p>
                    </div>
                  )}
                  {windowType.glassThickness_in && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Thickness</p>
                      <p className="text-lg font-medium">
                        {formatInchesToFtIn(windowType.glassThickness_in)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Manufacturer</p>
                  <p className="text-lg font-medium" data-testid="manufacturer">{windowType.manufacturer}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Model</p>
                  <p className="text-lg font-medium" data-testid="model">{windowType.model}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Frame Material</p>
                  <p className="text-lg">{windowType.frameMaterial}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Operation Type</p>
                  <p className="text-lg">{windowType.operationType}</p>
                </div>
              </div>

              <div className="space-y-4">
                {windowType.glassManufacturer && (
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Manufacturer</p>
                    <p className="text-lg">{windowType.glassManufacturer}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Type</p>
                  <p className="text-lg">{windowType.glassType}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Build</p>
                  <p className="text-lg">{windowType.glassBuild}</p>
                </div>
                {windowType.glassTypes && windowType.glassTypes.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Glass Features</p>
                    <div className="flex flex-wrap gap-2">
                      {windowType.glassTypes.map((feature, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Hardware</p>
                  <p className="text-lg">{windowType.hardware}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Warranty</p>
                  <p className="text-lg font-semibold text-green-600 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    {windowType.warrantyYears} Years
                  </p>
                </div>
              </div>
            </div>

            {/* Thermal Performance */}
            {(windowType.uValue || windowType.cr) && (
              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4">Thermal Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {windowType.uValue && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">U Value</p>
                      <p className="text-lg">{windowType.uValue} Btu/hr·ft²·°F</p>
                    </div>
                  )}
                  {windowType.cr && (
                    <div>
                      <p className="text-sm font-semibold text-slate-500 uppercase mb-1">CR</p>
                      <p className="text-lg">{windowType.cr}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Finishes */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-4">Finishes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Interior Finish</p>
                  <p className="text-lg">{windowType.finishInterior}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-1">Exterior Finish</p>
                  <p className="text-lg">{windowType.finishExterior}</p>
                </div>
              </div>
            </div>

            {/* Manuals */}
            {(windowType.installationManual || windowType.cleaningManual) && (
              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4">Documentation</h3>
                <div className="flex flex-wrap gap-3">
                  {windowType.installationManual && (
                    <Button variant="outline" data-testid="installation-manual">
                      <FileText className="h-4 w-4 mr-2" />
                      Installation Manual
                    </Button>
                  )}
                  {windowType.cleaningManual && (
                    <Button variant="outline" data-testid="cleaning-manual">
                      <FileText className="h-4 w-4 mr-2" />
                      Cleaning Manual
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Issue */}
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg mb-1">Found an Issue?</h3>
                <p className="text-sm text-slate-600">Report problems with this window</p>
              </div>
              <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="report-issue-button">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Report Issue
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Report an Issue</DialogTitle>
                    <DialogDescription>
                      Describe the problem you've found with this window
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleReportIssue} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Your Name (Optional)</Label>
                      <Input
                        data-testid="reporter-name-input"
                        value={reportData.reportedByName}
                        onChange={(e) => setReportData({ ...reportData, reportedByName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Your Email (Optional)</Label>
                      <Input
                        type="email"
                        data-testid="reporter-email-input"
                        value={reportData.reportedByEmail}
                        onChange={(e) => setReportData({ ...reportData, reportedByEmail: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Issue Description *</Label>
                      <Textarea
                        data-testid="issue-description-input"
                        placeholder="Describe the issue..."
                        rows={4}
                        value={reportData.description}
                        onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-report">
                      Submit Report
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="mt-12 py-6 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-600">
          <p>Powered by Specard - Window Management System</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicSpecard;